import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Avatar,
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Tabs,
  Tag,
  Upload,
  Typography,
} from "antd";
import {
  CameraOutlined,
  EditOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  SaveOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { useAuth } from "contexts/AuthContext";
import useUsers from "hooks/userManagement/useUsers";
import { useFileUpload } from "hooks/useFileUpload";
import apiClient from "utils/apiClient";
import {
  GENDER_OPTIONS,
  STATUS_OPTIONS,
  cleanValue,
  formatDateTime,
  getDisplayName,
} from "./profileUtils";

const fieldGroups = [
  {
    title: "Identity",
    description: "Edit your core account details one field at a time.",
    fields: [
      { name: "firstName", label: "First Name", icon: <UserOutlined /> },
      { name: "middleName", label: "Middle Name", icon: <UserOutlined /> },
      { name: "lastName", label: "Last Name", icon: <UserOutlined /> },
      { name: "username", label: "Username", icon: <UserOutlined />, editable: false },
    ],
  },
  {
    title: "Contact",
    description: "Keep your contact details accurate and up to date.",
    fields: [
      { name: "email", label: "Email", icon: <MailOutlined />, editable: false },
      { name: "phone", label: "Phone", icon: <PhoneOutlined /> },
    ],
  },
  {
    title: "Profile",
    description: "Personal attributes used throughout the PM tool.",
    fields: [
      { name: "gender", label: "Gender", type: "select", options: GENDER_OPTIONS },
      { name: "status", label: "Status", type: "select", options: STATUS_OPTIONS },
    ],
  },
];

const readPhotoUrl = async (value, getFileMetadata) => {
  const raw = cleanValue(value);
  if (!raw) return null;
  if (/^(https?:|data:|blob:)/i.test(raw)) return raw;
  if (!getFileMetadata) return null;

  try {
    const meta = await getFileMetadata(raw);
    return meta?.publicUrl || meta?.url || meta?.fileUrl || null;
  } catch {
    return null;
  }
};

const FieldCard = ({
  label,
  value,
  type = "text",
  options = [],
  icon,
  helperText,
  editable = true,
  onSave,
  loading = false,
}) => {
  const [editing, setEditing] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [draft, setDraft] = useState(cleanValue(value));
  const hoverSupported =
    typeof window !== "undefined"
      ? window.matchMedia?.("(hover: hover)")?.matches ?? true
      : true;

  useEffect(() => {
    setDraft(cleanValue(value));
  }, [value]);

  const displayValue = useMemo(() => {
    if (type === "select") {
      const option = options.find((item) => String(item.value) === String(value));
      return option?.label || cleanValue(value) || "Not provided";
    }
    return cleanValue(value) || "Not provided";
  }, [options, type, value]);

  const handleSave = async () => {
    if (!onSave) return;
    const nextValue = type === "select" ? draft || "" : draft;
    if (cleanValue(nextValue) === cleanValue(value)) {
      setEditing(false);
      return;
    }
    await onSave(nextValue);
    setEditing(false);
  };

  return (
    <Card
      size="small"
      className="custom-shadow"
      style={{
        borderRadius: 14,
        border: "1px solid #e5e7eb",
        background: "#fff",
      }}
      bodyStyle={{ padding: 14 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        className="d-flex align-items-center gap-3"
        style={{ minHeight: 44 }}
      >
        <div
          className="d-flex align-items-center gap-2"
          style={{ minWidth: 170, flexShrink: 0 }}
        >
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              background: "#f8fafc",
              border: "1px solid #e5e7eb",
              display: "grid",
              placeItems: "center",
              color: "#475569",
              flexShrink: 0,
            }}
          >
            {icon || <UserOutlined />}
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a", lineHeight: 1.1 }}>
              {label}
            </div>
            {helperText && <div style={{ fontSize: 12, color: "#64748b" }}>{helperText}</div>}
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          {!editing ? (
            <div
              style={{
                fontSize: 14,
                fontWeight: 500,
                color: "#111827",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {displayValue}
            </div>
          ) : type === "select" ? (
            <Select
              value={draft || undefined}
              options={options}
              onChange={(nextValue) => setDraft(nextValue || "")}
              style={{ width: "100%" }}
            />
          ) : (
            <Input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={label}
              autoComplete="off"
            />
          )}
        </div>

        {editable && onSave && (
          <Button
            type="text"
            size="small"
            icon={editing ? <SaveOutlined /> : <EditOutlined />}
            loading={loading}
            onClick={editing ? handleSave : () => setEditing(true)}
            aria-label={editing ? `Save ${label}` : `Edit ${label}`}
            title={editing ? `Save ${label}` : `Edit ${label}`}
            style={{
              color: "#0f172a",
              marginLeft: "auto",
              opacity: hoverSupported ? (hovered || editing ? 1 : 0) : 1,
              pointerEvents: hoverSupported ? (hovered || editing ? "auto" : "none") : "auto",
              transition: "opacity 0.15s ease",
            }}
          />
        )}
      </div>
    </Card>
  );
};

const PhotoCard = ({
  title,
  description,
  preview,
  onUpload,
  loading = false,
  badge,
}) => (
  <Card
    className="custom-shadow h-100"
    style={{ borderRadius: 16, border: "1px solid #e5e7eb" }}
    bodyStyle={{ padding: 18 }}
  >
    <div className="d-flex align-items-start justify-content-between gap-3">
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{title}</div>
        <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>{description}</div>
      </div>
      {badge}
    </div>

    <div className="d-flex flex-column align-items-center justify-content-center text-center mt-3">
      <Avatar
        size={96}
        src={preview || undefined}
        icon={!preview && <UserOutlined />}
        style={{
          background: preview ? undefined : "linear-gradient(135deg, #111827, #475569)",
          color: "#fff",
          fontSize: 30,
          marginBottom: 14,
        }}
      >
        {!preview ? "U" : null}
      </Avatar>

      <Upload beforeUpload={() => false} showUploadList={false} accept="image/*" onChange={({ file }) => onUpload(file.originFileObj || file)}>
        <Button icon={<CameraOutlined />} loading={loading}>
          Update photo
        </Button>
      </Upload>
    </div>
  </Card>
);

const PasswordCard = ({ onSubmit, loading = false, enabled = true }) => {
  const [form] = Form.useForm();

  if (!enabled) {
    return (
      <Card className="custom-shadow" style={{ borderRadius: 16 }}>
        <Typography.Text type="secondary">Password changes are only available for your own profile.</Typography.Text>
      </Card>
    );
  }

  const handleFinish = async (values) => {
    await onSubmit(values);
    form.resetFields();
  };

  return (
    <Card
      className="custom-shadow"
      style={{ borderRadius: 16, border: "1px solid #e5e7eb" }}
      bodyStyle={{ padding: 18 }}
    >
      <div className="d-flex align-items-center gap-2 mb-2">
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            background: "#f8fafc",
            border: "1px solid #e5e7eb",
            display: "grid",
            placeItems: "center",
          }}
        >
          <LockOutlined />
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Security</div>
          <div style={{ fontSize: 12, color: "#64748b" }}>Change your password and review security details.</div>
        </div>
      </div>

      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item label="Current password" name="currentPassword" rules={[{ required: true, message: "Current password is required" }]}>
              <Input.Password />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="New password" name="newPassword" rules={[{ required: true, message: "New password is required" }]}>
              <Input.Password />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              label="Confirm password"
              name="confirmPassword"
              dependencies={["newPassword"]}
              rules={[
                { required: true, message: "Confirm your password" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("newPassword") === value) return Promise.resolve();
                    return Promise.reject(new Error("Passwords do not match"));
                  },
                }),
              ]}
            >
              <Input.Password />
            </Form.Item>
          </Col>
        </Row>

        <div className="d-flex justify-content-end">
          <Button type="primary" htmlType="submit" loading={loading}>
            Update Password
          </Button>
        </div>
      </Form>
    </Card>
  );
};

const UserProfilePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();
  const { user: authUser, refreshAuth } = useAuth();
  const targetUserId = userId || authUser?.id;
  const isOwnProfile = !userId || String(userId) === String(authUser?.id);
  const [form] = Form.useForm();
  const { getUserById, patchUserProperties, patchProfileProperties } = useUsers(
    0,
    20,
    null,
    true,
    { autoFetch: false },
  );
  const { uploadFile, getFileMetadata, uploading } = useFileUpload();

  const [loading, setLoading] = useState(true);
  const [savingField, setSavingField] = useState({});
  const [savingPassword, setSavingPassword] = useState(false);
  const [profile, setProfile] = useState(null);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const showSecurityTab = location.state?.activeTab === "security";
  const [activeTab, setActiveTab] = useState(showSecurityTab ? "security" : "overview");

  const loadProfile = useCallback(async () => {
    if (!targetUserId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const fresh = await getUserById(targetUserId);
      setProfile(fresh || null);
      form.setFieldsValue({
        firstName: cleanValue(fresh?.firstName),
        middleName: cleanValue(fresh?.middleName),
        lastName: cleanValue(fresh?.lastName),
        username: cleanValue(fresh?.username),
        email: cleanValue(fresh?.email),
        phone: cleanValue(fresh?.phone),
        gender: cleanValue(fresh?.gender),
        status: cleanValue(fresh?.status || "ACTIVE"),
      });

      setProfilePhoto(await readPhotoUrl(fresh?.profilePhoto, getFileMetadata));
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || "Failed to load profile");
    } finally {
      setLoading(false);
    }
  }, [form, getFileMetadata, getUserById, targetUserId]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    if (showSecurityTab) {
      setActiveTab("security");
    }
  }, [showSecurityTab]);

  const saveField = useCallback(
    async (fieldName, nextValue) => {
      if (!profile?.id) return;

      const normalized = cleanValue(nextValue).trim();
      const currentValue = cleanValue(profile?.[fieldName]).trim();
      if (normalized === currentValue) return;

      setSavingField((prev) => ({ ...prev, [fieldName]: true }));
      try {
        if (fieldName === "email") {
          await patchProfileProperties(profile.id, [
            {
              name: "contactDetails",
              value: {
                mobilePhone: cleanValue(profile?.phone) || null,
                personalEmail: normalized || null,
                email: normalized || null,
              },
            },
          ]);
        } else if (["gender", "status"].includes(fieldName)) {
          await patchProfileProperties(profile.id, [{ name: fieldName, value: normalized || null }]);
        } else {
          await patchUserProperties(profile.id, [{ name: fieldName, value: normalized }]);
        }

        setProfile((prev) => ({
          ...prev,
          [fieldName]: fieldName === "email" ? normalized : normalized,
          contactDetails:
            fieldName === "email"
              ? {
                  ...(prev?.contactDetails || {}),
                  mobilePhone: cleanValue(prev?.phone) || null,
                  personalEmail: normalized || null,
                  email: normalized || null,
                }
              : prev?.contactDetails,
        }));

        if (isOwnProfile) {
          await refreshAuth();
        }

        toast.success(`${fieldName} updated successfully`);
      } catch (error) {
        toast.error(error?.response?.data?.message || error.message || `Failed to update ${fieldName}`);
      } finally {
        setSavingField((prev) => ({ ...prev, [fieldName]: false }));
      }
    },
    [isOwnProfile, patchProfileProperties, patchUserProperties, profile, refreshAuth],
  );

  const savePhoto = useCallback(
    async (fieldName, file) => {
      if (!profile?.id || !file) return;

      const previewUrl = URL.createObjectURL(file);
      if (fieldName === "profilePhoto") setProfilePhoto(previewUrl);

      try {
        const upload = await uploadFile(profile.id, file, {
          purpose: "USER_PROFILE",
          fileType: "PHOTO",
        });
        const fileId = upload?.fileId || upload?.file?.id || upload?.file?.fileId || null;
        if (!fileId) throw new Error("No file id returned from upload");

        await patchProfileProperties(profile.id, [{ name: fieldName, value: fileId }]);

        const resolved = await readPhotoUrl(fileId, getFileMetadata);
        if (fieldName === "profilePhoto") setProfilePhoto(resolved || previewUrl);

        setProfile((prev) => ({ ...prev, [fieldName]: resolved || fileId }));
        if (isOwnProfile) {
          await refreshAuth();
        }
        toast.success("Profile photo updated");
      } catch (error) {
        toast.error(error?.response?.data?.message || error.message || "Failed to update photo");
      } finally {
        // Keep the preview URL alive until the next upload or navigation.
      }
    },
    [getFileMetadata, isOwnProfile, patchProfileProperties, profile?.id, refreshAuth, uploadFile],
  );

  const handlePasswordSubmit = async (values) => {
    setSavingPassword(true);
    try {
      await apiClient.put("/identity-service/api/v1/auth/password", {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      toast.success("Password updated successfully");
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || "Failed to update password");
    } finally {
      setSavingPassword(false);
    }
  };

  const profileRows = useMemo(
    () =>
      fieldGroups.map((group) => ({
        ...group,
        fields: group.fields.map((field) => ({
          ...field,
          value:
            field.name === "email"
              ? cleanValue(profile?.email || profile?.contactDetails?.personalEmail || profile?.contactDetails?.email)
              : cleanValue(profile?.[field.name]),
        })),
      })),
    [profile],
  );

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 420 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!profile) {
    return (
      <Card className="custom-shadow" style={{ borderRadius: 16 }}>
        <Typography.Text type="danger">Profile not found.</Typography.Text>
      </Card>
    );
  }

  return (
    <div className="project-page">
      <div className="d-flex justify-content-between align-items-start gap-3 mb-4">
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".08em", color: "#94a3b8", textTransform: "uppercase" }}>
            {isOwnProfile ? "My profile" : "User profile"}
          </div>
          <h1 style={{ margin: "4px 0 6px", fontSize: 24, fontWeight: 700, color: "#0f172a" }}>
            {getDisplayName(profile)}
          </h1>
          <div style={{ color: "#64748b", fontSize: 13 }}>
            Edit core profile fields, photos, and security settings in one place.
          </div>
        </div>

        <Space wrap>
          <Button onClick={() => navigate(-1)}>Go back</Button>
        </Space>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            className="custom-tabs bg-white border rounded-4 px-2 pt-2"
            items={[
              {
                key: "overview",
                label: "Overview",
                children: (
                  <div className="py-3">
                    <Row gutter={[16, 16]}>
                      <Col xs={24} md={12}>
                        <PhotoCard
                          title="Profile photo"
                          description="This is the public image shown across the PM tool."
                          preview={profilePhoto}
                          onUpload={(file) => savePhoto("profilePhoto", file)}
                          loading={uploading}
                          badge={
                            <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                              Public
                            </Tag>
                          }
                        />
                      </Col>
                    </Row>

                    <div className="mt-4" style={{ display: "grid", gap: 16 }}>
                      {profileRows.map((group) => (
                        <div key={group.title}>
                          <div className="d-flex align-items-end justify-content-between mb-2">
                            <div>
                              <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>{group.title}</div>
                              <div style={{ fontSize: 12, color: "#64748b" }}>{group.description}</div>
                            </div>
                          </div>

                          <Row gutter={[16, 16]}>
                            {group.fields.map((field) => (
                              <Col xs={24} md={12} key={field.name}>
                              <FieldCard
                                label={field.label}
                                icon={field.icon}
                                value={field.value}
                                type={field.type}
                                options={field.options || []}
                                editable={field.editable !== false}
                                helperText={field.name === "username" ? "Used to sign in and identify the account." : undefined}
                                loading={Boolean(savingField[field.name])}
                                onSave={(nextValue) => saveField(field.name, nextValue)}
                              />
                              </Col>
                            ))}
                          </Row>
                        </div>
                      ))}

                      <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                          <Card className="custom-shadow" style={{ borderRadius: 14, border: "1px solid #e5e7eb" }}>
                            <div style={{ fontSize: 12, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".08em" }}>Created At</div>
                            <div style={{ marginTop: 6, fontSize: 14, fontWeight: 600, color: "#111827" }}>{formatDateTime(profile.createdAt)}</div>
                          </Card>
                        </Col>
                        <Col xs={24} md={12}>
                          <Card className="custom-shadow" style={{ borderRadius: 14, border: "1px solid #e5e7eb" }}>
                            <div style={{ fontSize: 12, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".08em" }}>Updated At</div>
                            <div style={{ marginTop: 6, fontSize: 14, fontWeight: 600, color: "#111827" }}>{formatDateTime(profile.updatedAt)}</div>
                          </Card>
                        </Col>
                      </Row>
                    </div>
                  </div>
                ),
              },
              ...(showSecurityTab
                ? [
                    {
                      key: "security",
                      label: "Account Settings",
                      children: (
                        <div className="py-3">
                          <Row gutter={[16, 16]}>
                            <Col xs={24} md={12}>
                              <PasswordCard
                                enabled={isOwnProfile}
                                onSubmit={handlePasswordSubmit}
                                loading={savingPassword}
                              />
                            </Col>
                          </Row>
                        </div>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        </Col>
      </Row>
    </div>
  );
};

export default UserProfilePage;
